import router from './router'
import { useAppStoreWithOut } from '@/store/modules/app'
import type { RouteRecordRaw } from 'vue-router'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { usePageLoading } from '@/hooks/web/usePageLoading'
import { NO_REDIRECT_WHITE_LIST } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { getUserAppCustomRouteApi } from '@/api/admin/user/permission'
import { isAdminSystemUrl, isAdminUserType } from '@/utils/routerHelper'

const { start, done } = useNProgress()

const { loadStart, loadDone } = usePageLoading()

/**
 * 检查路由是否在白名单中
 * 支持精确匹配、前缀匹配和正则匹配
 * @param path 当前路由路径
 * @param whiteList 白名单
 * @returns 是否在白名单中
 */
const isInWhiteList = (path: string, whiteList: string[]): boolean => {
  return whiteList.some((item) => {
    // 精确匹配
    if (item === path) {
      return true
    }
    // 前缀匹配（以/*结尾）
    if (item.endsWith('/*')) {
      const prefix = item.slice(0, -2)
      return path.startsWith(prefix)
    }
    // 正则匹配（以/^开头，以$/结尾）
    if (item.startsWith('/^') && item.endsWith('/$')) {
      const regex = new RegExp(item.slice(2, -2))
      return regex.test(path)
    }
    // 默认前缀匹配
    return path.startsWith(item)
  })
}

router.beforeEach(async (to, from, next) => {
  start()
  loadStart()
  const permissionStore = usePermissionStoreWithOut()
  const appStore = useAppStoreWithOut()
  const userStore = useUserStoreWithOut()
  if (userStore.getAccountInfo) {
    const canSelectTenant =
      to.path === '/admin/tenant/tenant-select' && userStore.getHasTenantAccess
    if (
      isAdminSystemUrl(to.path) &&
      !isAdminUserType(userStore.getUserInfo?.type) &&
      !canSelectTenant
    ) {
      next('/')
      return
    }
    if (to.path === '/login') {
      next({ path: '/' })
    } else {
      if (permissionStore.getIsAddRouters) {
        next()
        return
      }
      try {
        // 静态路由无需请求菜单；动态路由必须区分空权限和菜单获取失败。
        if (appStore.getDynamicRouter) {
          const roleRouters = await getUserAppCustomRouteApi(true)
          await permissionStore.generateRoutes('server', roleRouters)
        } else {
          await permissionStore.generateRoutes('static')
        }
      } catch (error) {
        console.error('加载用户路由失败:', error)
        done()
        loadDone()
        next(false)
        return
      }

      permissionStore.getAddRouters.forEach((route) => {
        router.addRoute(route as unknown as RouteRecordRaw) // 动态添加可访问路由表
      })
      const redirectPath = from.query.redirect || to.path
      const redirect = decodeURIComponent(redirectPath as string)
      const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect }
      permissionStore.setIsAddRouters(true)
      next(nextData)
    }
  } else {
    if (isInWhiteList(to.path, NO_REDIRECT_WHITE_LIST)) {
      next()
    } else {
      next(`/login?redirect=${to.path}`) // 否则全部重定向到登录页
    }
  }
})

router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
  done() // 结束Progress
  loadDone()
})
